/**
 * Query keys for everything the user screens load.
 *
 * They live in one place because the panels are independent — the roles editor, the credits list
 * and the subscription card each own their own mutation — and a mutation in one has to be able to
 * invalidate what another one is showing. Keys built ad hoc drift apart the moment two of them
 * spell the same thing differently, and the symptom (a panel that keeps showing the value you
 * just changed) looks like a server bug.
 */

/** The list page. Every user mutation invalidates this, since any of them can change a row. */
export const ADMIN_USERS_KEY = ['admin-users']

/** One user, as loaded by the detail page. */
export const adminUserKey = (userId: string) => ['admin-user', userId]

export const adminUserConnectionsKey = (userId: string) => ['admin-user-connections', userId]

export const adminUserCreditsKey = (userId: string) => ['admin-user-credits', userId]

export const adminUserSubscriptionKey = (userId: string) => ['admin-user-subscription', userId]

export const adminUserIpAddressesKey = (userId: string) => ['admin-user-ip-addresses', userId]

/** The ban list page and every "is this address banned" chip. */
export const IP_BANS_KEY = ['ip-bans']

/**
 * The user data editor's reads (Backlog 050) all share the `adminUserDataKey(userId)` prefix.
 *
 * A change to one kind of data routinely moves another — voiding a run deletes its step event and
 * can take an achievement, deleting a trail changes lifetime distance — so every editor mutation
 * invalidates the whole prefix (plus the user) rather than guessing which lists it touched.
 */
export const adminUserDataKey = (userId: string) => ['admin-user-data', userId]

export const adminUserAchievementsKey = (userId: string) => [...adminUserDataKey(userId), 'achievements']

export const adminUserSessionsKey = (userId: string) => [...adminUserDataKey(userId), 'sessions']

/** Prefix; the query appends page and page size. */
export const adminUserStepEventsKey = (userId: string) => [...adminUserDataKey(userId), 'step-events']

export const adminUserTrailsKey = (userId: string) => [...adminUserDataKey(userId), 'trails']

export const adminUserReviewsKey = (userId: string) => [...adminUserDataKey(userId), 'reviews']

/** Prefix; the query appends page and page size. */
export const adminUserActivitiesKey = (userId: string) => [...adminUserDataKey(userId), 'activities']

/** Stories and chapters the user owns (purchased, granted or gifted), live and revoked. */
export const adminUserEntitlementsKey = (userId: string) => ['admin-user-entitlements', userId]
